import { Divider, Select, DatePicker } from "antd";
import Title from "antd/es/typography/Title";
import dayjs, { Dayjs } from "dayjs";
import { useFormikContext } from "formik";
import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetAllProductCardsWithNoParamsQuery } from "shared/api";
import { useGetAllCompaniesQuery } from "shared/api/suppliers/suppliersApi";
import { selectFiltering } from "shared/helpers";
import { Colors } from "shared/themes";
import { ExecuteReportValues } from "shared/types";
import { Loader } from "shared/ui";

interface ReelLabResultMultipleFilterProps {
  selectedCompanyId: number | null;
  onCompanyChange: (value: number | null) => void;
  selectedProductCardIds: number[];
  onProductCardChange: (value: number[]) => void;
}

const ReelLabResultMultipleFilter: FC<ReelLabResultMultipleFilterProps> = ({
  selectedCompanyId,
  onCompanyChange,
  selectedProductCardIds,
  onProductCardChange,
}) => {
  const { t } = useTranslation("", { keyPrefix: "executeReports" });

  const { values, setFieldValue } = useFormikContext<ExecuteReportValues>();

  const handleDateChange = (
    field: keyof ExecuteReportValues,
    val: Dayjs | null
  ) => {
    setFieldValue(field, val ? val.toISOString() : null);
  };

  const handleCompanyChange = (value: number | null) => {
    setFieldValue("company_id", value);
    setFieldValue("product_card_id", null);
    setFieldValue("product_card_list_id", []);
    onCompanyChange(value);
  };

  const handleProductCardChange = (value: number[]) => {
    setFieldValue("product_card_list_id", value);
    setFieldValue("product_card_id", value.length === 1 ? value[0] : null);
    onProductCardChange(value);
  };

  const {
    data: companies,
    isLoading: isLoadingCompanies,
    isFetching: isFetchingCompanies,
  } = useGetAllCompaniesQuery({});

  const adaptedCompanies = useMemo(() => {
    return (
      companies?.map((company) => ({
        label: company.name,
        value: company.id,
      })) || []
    );
  }, [companies]);

  const {
    data: productCards,
    isLoading: isLoadingProductCards,
    isFetching: isFetchingProductCards,
  } = useGetAllProductCardsWithNoParamsQuery(
    { company: selectedCompanyId ?? undefined },
    { skip: !selectedCompanyId }
  );

  const adaptedProductCards = useMemo(() => {
    return (
      productCards?.map((productCard) => ({
        label: productCard.name,
        value: productCard.id,
      })) || []
    );
  }, [productCards]);

  if (
    isLoadingCompanies ||
    isFetchingCompanies ||
    (selectedCompanyId && (isLoadingProductCards || isFetchingProductCards))
  ) {
    return <Loader type="fullscreen-backdrop" />;
  }

  return (
    <div className="flex flex-col gap-y-3 mt-2">
      {/* First row: Company and Product Card */}
      <div className="flex gap-x-3">
        <div className="flex gap-x-2">
          <Title level={5} className="!w-[180px] !m-0 !text-[14px]">
            {t("selectCompany")}
          </Title>
          {" - "}
          <Select
            loading={isLoadingCompanies}
            value={selectedCompanyId}
            placeholder={t("selectCompany")}
            options={adaptedCompanies}
            onChange={handleCompanyChange}
            className="w-full"
            size="small"
            showSearch
            filterOption={(input, option) => selectFiltering(input, option)}
          />
        </div>

        {selectedCompanyId && (
          <>
            <Divider
              type="vertical"
              className="h-full"
              style={{ background: Colors.blue.primary }}
            />

            <div className="flex gap-x-2">
              <Title level={5} className="!w-[260px] !m-0 !text-[14px]">
                {t("selectProductCard")}
              </Title>
              {" - "}
              <Select
                mode="multiple"
                loading={isLoadingProductCards}
                value={selectedProductCardIds}
                placeholder={t("selectProductCard")}
                options={adaptedProductCards}
                onChange={handleProductCardChange}
                className="w-full min-w-[300px]"
                size="small"
                showSearch
                filterOption={(input, option) => selectFiltering(input, option)}
              />
            </div>
          </>
        )}
      </div>

      {/* Second row: Date pickers */}
      {selectedProductCardIds.length > 0 && (
        <div className="flex gap-x-3">
          <div className="flex gap-x-2">
            <Title level={5} className="!w-[80px] !m-0 !text-[14px]">
              {t("startDate")}
            </Title>
            {" - "}
            <DatePicker
              showTime
              size="small"
              value={
                values?.quality_result_material_item_timestamp_start
                  ? dayjs(values.quality_result_material_item_timestamp_start)
                  : null
              }
              onChange={(val) =>
                handleDateChange(
                  "quality_result_material_item_timestamp_start",
                  val
                )
              }
              onOk={(val) =>
                handleDateChange(
                  "quality_result_material_item_timestamp_start",
                  val
                )
              }
            />
          </div>

          <Divider
            type="vertical"
            className="h-full"
            style={{ background: Colors.blue.primary }}
          />

          <div className="flex gap-x-2">
            <Title level={5} className="!w-[80px] !m-0 !text-[14px]">
              {t("endDate")}
            </Title>
            {" - "}
            <DatePicker
              showTime
              size="small"
              value={
                values?.quality_result_material_item_timestamp_end
                  ? dayjs(values.quality_result_material_item_timestamp_end)
                  : null
              }
              onChange={(val) =>
                handleDateChange(
                  "quality_result_material_item_timestamp_end",
                  val
                )
              }
              onOk={(val) =>
                handleDateChange(
                  "quality_result_material_item_timestamp_end",
                  val
                )
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

export { ReelLabResultMultipleFilter };
